import {
  getScrollContainer,
  getScrollEventTarget,
  getScrollTop,
  scrollTo as scrollContainerTo,
} from '@theme/scroll-container';

class FaqCategoriesSection extends HTMLElement {
  connectedCallback() {
    this.tabBar = this.querySelector('[data-faq-tab-bar]');
    this.tabsWrap = this.querySelector('.faq-categories__tabs-wrap');
    this.categories = Array.from(this.querySelectorAll('[data-faq-category]'));
    this.tabs = [];
    this.isProgrammaticScroll = false;
    this.scrollSpyTimeout = null;
    this.programmaticScrollReleaseTimeout = null;
    this.programmaticScrollFallbackTimeout = null;
    this.lastActiveCategoryId = null;
    this.onScrollSpy = null;
    this.onProgrammaticScroll = null;
    this.tabsDragMoved = false;
    this.tabsDragPointerId = null;
    this.tabsDragAxis = null;

    if (!this.tabBar || !this.categories.length) {
      return;
    }

    this.buildTabs();
    this.setScrollMargin();
    this.initAccordionGroups();
    this.bindAccordionSingleOpen();
    this.bindTabClicks();
    this.bindTabsDragScroll();
    this.bindScrollSpy();
    this.syncFromHash();

    window.addEventListener('hashchange', this.onHashChange);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    if (this.onScrollSpy) {
      getScrollEventTarget().removeEventListener('scroll', this.onScrollSpy);
    }

    if (this.onProgrammaticScroll) {
      getScrollEventTarget().removeEventListener('scroll', this.onProgrammaticScroll);
    }

    window.clearTimeout(this.scrollSpyTimeout);
    window.clearTimeout(this.programmaticScrollReleaseTimeout);
    window.clearTimeout(this.programmaticScrollFallbackTimeout);
    window.removeEventListener('hashchange', this.onHashChange);
    window.removeEventListener('resize', this.onResize);
    this.unbindTabsDragScroll();
  }

  onHashChange = () => {
    this.syncFromHash(true);
  };

  onResize = () => {
    this.setScrollMargin();
    this.updateActiveCategoryFromScroll();
  };

  buildTabs() {
    this.tabBar.innerHTML = '';

    this.categories.forEach((category, index) => {
      const categoryId = category.id;
      if (!categoryId) {
        return;
      }

      const label =
        category.dataset.tabLabel ||
        category.querySelector('.faq-categories__category-heading .text-block')?.textContent?.trim() ||
        category.querySelector('.faq-categories__category-heading')?.textContent?.trim() ||
        `Category ${index + 1}`;

      const tab = document.createElement('a');
      tab.href = `#${categoryId}`;
      tab.className = 'faq-categories__tab';
      tab.dataset.faqTab = categoryId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      tab.innerHTML = `<span class="faq-categories__tab-label">${label}</span>`;

      if (index === 0) {
        tab.classList.add('is-active');
        this.lastActiveCategoryId = categoryId;
      }

      this.tabBar.appendChild(tab);
    });

    this.tabs = Array.from(this.tabBar.querySelectorAll('[data-faq-tab]'));
  }

  setScrollMargin() {
    const stickyOffset = this.getStickyOffset();
    this.style.setProperty('--faq-categories-scroll-offset', `${stickyOffset}px`);

    this.categories.forEach((category) => {
      category.style.scrollMarginTop = `${stickyOffset}px`;
    });
  }

  syncFromHash(shouldScroll = false) {
    const hash = window.location.hash.replace('#', '');

    if (!hash) {
      this.setActiveTab(this.categories[0]?.id);
      return;
    }

    const matchingCategory = this.categories.find((category) => category.id === hash);
    if (!matchingCategory) {
      return;
    }

    this.setActiveTab(matchingCategory.id);

    if (shouldScroll) {
      this.scrollToCategory(matchingCategory);
    }
  }

  bindTabsDragScroll() {
    const shell = this.querySelector('.faq-categories__tabs-shell');
    const wrap = this.tabsWrap;

    if (!shell || !wrap) {
      return;
    }

    this.tabsDragShell = shell;
    this.tabsDragStartX = 0;
    this.tabsDragStartY = 0;
    this.tabsDragStartScrollLeft = 0;

    shell.addEventListener('pointerdown', this.onTabsPointerDown);
    shell.addEventListener('pointermove', this.onTabsPointerMove, { passive: false });
    shell.addEventListener('pointerup', this.onTabsPointerUp);
    shell.addEventListener('pointercancel', this.onTabsPointerUp);
  }

  unbindTabsDragScroll() {
    const shell = this.tabsDragShell;

    if (!shell) {
      return;
    }

    shell.removeEventListener('pointerdown', this.onTabsPointerDown);
    shell.removeEventListener('pointermove', this.onTabsPointerMove);
    shell.removeEventListener('pointerup', this.onTabsPointerUp);
    shell.removeEventListener('pointercancel', this.onTabsPointerUp);
    this.tabsDragShell = null;
  }

  onTabsPointerDown = (event) => {
    if (!this.tabsWrap || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }

    this.tabsDragPointerId = event.pointerId;
    this.tabsDragAxis = null;
    this.tabsDragMoved = false;
    this.tabsDragStartX = event.clientX;
    this.tabsDragStartY = event.clientY;
    this.tabsDragStartScrollLeft = this.tabsWrap.scrollLeft;
  };

  onTabsPointerMove = (event) => {
    if (this.tabsDragPointerId !== event.pointerId || !this.tabsWrap) {
      return;
    }

    const deltaX = event.clientX - this.tabsDragStartX;
    const deltaY = event.clientY - this.tabsDragStartY;

    if (!this.tabsDragAxis) {
      if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) {
        return;
      }

      this.tabsDragAxis = Math.abs(deltaX) >= Math.abs(deltaY) ? 'x' : 'y';

      if (this.tabsDragAxis === 'x') {
        this.tabsWrap.classList.add('is-dragging');
        this.tabsDragShell?.setPointerCapture(event.pointerId);
      }
    }

    if (this.tabsDragAxis !== 'x') {
      return;
    }

    event.preventDefault();
    this.tabsDragMoved = true;
    this.tabsWrap.scrollLeft = this.tabsDragStartScrollLeft - deltaX;
  };

  onTabsPointerUp = (event) => {
    if (this.tabsDragPointerId !== event.pointerId) {
      return;
    }

    if (this.tabsDragMoved) {
      window.setTimeout(() => {
        this.tabsDragMoved = false;
      }, 0);
    }

    this.tabsWrap?.classList.remove('is-dragging');
    this.tabsDragPointerId = null;
    this.tabsDragAxis = null;
  };

  bindTabClicks() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();

        if (this.tabsDragMoved) {
          return;
        }

        const targetId = tab.dataset.faqTab;
        const target = this.categories.find((category) => category.id === targetId);

        if (!target || !targetId) {
          return;
        }

        this.setActiveTab(targetId);
        this.scrollToCategory(target);
        this.updateHash(targetId);
      });
    });
  }

  updateHash(targetId) {
    const nextHash = `#${targetId}`;

    if (window.location.hash === nextHash) {
      return;
    }

    history.replaceState(null, '', nextHash);
  }

  getScrollTopForTarget(target) {
    const stickyOffset = this.getStickyOffset();
    return target.getBoundingClientRect().top + getScrollTop() - stickyOffset;
  }

  getMaxScrollTop() {
    const scrollContainer = getScrollContainer();
    return Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
  }

  scrollToCategory(target) {
    this.isProgrammaticScroll = true;
    window.clearTimeout(this.programmaticScrollReleaseTimeout);
    window.clearTimeout(this.programmaticScrollFallbackTimeout);

    const scrollTarget = getScrollEventTarget();

    if (this.onProgrammaticScroll) {
      scrollTarget.removeEventListener('scroll', this.onProgrammaticScroll);
    }

    this.onProgrammaticScroll = () => {
      window.clearTimeout(this.programmaticScrollReleaseTimeout);
      this.programmaticScrollReleaseTimeout = window.setTimeout(() => {
        this.finishProgrammaticScroll(target.id);
      }, 120);
    };

    scrollTarget.addEventListener('scroll', this.onProgrammaticScroll, { passive: true });

    const targetTop = Math.min(Math.max(0, this.getScrollTopForTarget(target)), this.getMaxScrollTop());

    scrollContainerTo({
      top: targetTop,
      behavior: 'smooth',
    });

    this.programmaticScrollFallbackTimeout = window.setTimeout(() => {
      this.finishProgrammaticScroll(target.id);
    }, 1500);
  }

  finishProgrammaticScroll(targetId) {
    const scrollTarget = getScrollEventTarget();

    if (this.onProgrammaticScroll) {
      scrollTarget.removeEventListener('scroll', this.onProgrammaticScroll);
      this.onProgrammaticScroll = null;
    }

    window.clearTimeout(this.programmaticScrollReleaseTimeout);
    window.clearTimeout(this.programmaticScrollFallbackTimeout);
    this.isProgrammaticScroll = false;

    if (targetId) {
      this.setActiveTab(targetId);
    }
  }

  bindScrollSpy() {
    const scrollTarget = getScrollEventTarget();

    if (this.onScrollSpy) {
      scrollTarget.removeEventListener('scroll', this.onScrollSpy);
    }

    this.onScrollSpy = () => {
      if (this.isProgrammaticScroll) {
        return;
      }

      window.clearTimeout(this.scrollSpyTimeout);
      this.scrollSpyTimeout = window.setTimeout(() => {
        this.updateActiveCategoryFromScroll();
      }, 50);
    };

    scrollTarget.addEventListener('scroll', this.onScrollSpy, { passive: true });
    this.updateActiveCategoryFromScroll();
  }

  updateActiveCategoryFromScroll() {
    if (this.isProgrammaticScroll || !this.categories.length) {
      return;
    }

    const scrollTop = getScrollTop();
    const maxScroll = this.getMaxScrollTop();

    if (scrollTop >= maxScroll - 4) {
      const lastCategory = this.categories[this.categories.length - 1];
      if (lastCategory?.id && lastCategory.id !== this.lastActiveCategoryId) {
        this.setActiveTab(lastCategory.id);
      }
      return;
    }

    const viewportTop = this.getStickyOffset() + 8;
    const viewportBottom = window.innerHeight;
    let bestCategory = this.categories[0];
    let bestVisibleHeight = -1;

    for (const category of this.categories) {
      const rect = category.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, viewportTop);
      const visibleBottom = Math.min(rect.bottom, viewportBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      if (visibleHeight > bestVisibleHeight) {
        bestVisibleHeight = visibleHeight;
        bestCategory = category;
      }
    }

    if (bestCategory?.id && bestCategory.id !== this.lastActiveCategoryId) {
      this.setActiveTab(bestCategory.id);
    }
  }

  initAccordionGroups() {
    this.querySelectorAll('.faq-categories__accordion[data-accordion-group]').forEach((accordion) => {
      const groupName = accordion.dataset.accordionGroup;
      if (!groupName) {
        return;
      }

      const detailsList = Array.from(accordion.querySelectorAll('details'));
      detailsList.forEach((details) => {
        details.name = groupName;
      });

      const openDetails = detailsList.filter((details) => details.open);
      if (openDetails.length > 1) {
        openDetails.slice(1).forEach((details) => {
          details.open = false;
        });
      }
    });
  }

  bindAccordionSingleOpen() {
    this.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open) {
          return;
        }

        const accordion = details.closest('.faq-categories__accordion');
        if (!accordion || !this.contains(accordion)) {
          return;
        }

        accordion.querySelectorAll('details[open]').forEach((openDetails) => {
          if (openDetails === details) return;

          const host = openDetails.closest('accordion-custom');
          if (host && typeof host.shrink === 'function') {
            host.shrink();
            return;
          }

          openDetails.open = false;
        });
      },
      true
    );
  }

  getStickyOffset() {
    const tabsShell = this.querySelector('.faq-categories__tabs-shell');
    const tabsShellHeight = tabsShell?.offsetHeight || 0;
    const headerOffset = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );

    return (Number.isFinite(headerOffset) ? headerOffset : 72) + tabsShellHeight + 8;
  }

  setActiveTab(targetId) {
    if (!targetId) {
      return;
    }

    this.lastActiveCategoryId = targetId;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.faqTab === targetId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.scrollActiveTabIntoView(targetId);
  }

  scrollActiveTabIntoView(targetId) {
    const activeTab = this.tabs.find((tab) => tab.dataset.faqTab === targetId);

    if (!activeTab || !this.tabsWrap) {
      return;
    }

    const containerRect = this.tabsWrap.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const targetScrollLeft =
      this.tabsWrap.scrollLeft +
      (tabRect.left - containerRect.left) -
      containerRect.width / 2 +
      tabRect.width / 2;

    this.tabsWrap.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('faq-categories-section')) {
  customElements.define('faq-categories-section', FaqCategoriesSection);
}
